import { Link, useParams } from "react-router-dom";
import { AiOutlineEye, AiOutlineShoppingCart } from "react-icons/ai";
import { FiCreditCard, FiCopy, FiExternalLink, FiTruck } from "react-icons/fi";
import Nav from "../../components/home/Nav";
import AccountList from "../../components/home/AccountList";
import Spinner from "../../components/Spinner";
import Pagination from "../../components/Pagination";
import { useGetOrdersQuery } from "../../store/services/userOrdersService";
import { toman, toPersianNumber } from "../../utils/persian";
import { orderDetailUrl, orderRef } from "../../utils/orderUrl";
import { useRequestPaymentMutation } from "../../store/services/paymentService";
import { useFeedback } from "../../context/AppFeedbackContext";
import { useState } from "react";

const statusLabels = {
  pending: "در انتظار بررسی",
  processing: "در حال آماده‌سازی",
  shipped: "ارسال شده",
  delivered: "تحویل شده",
  cancelled: "لغو شده",
};

const paymentLabels = {
  unpaid: "پرداخت نشده",
  paid: "پرداخت شده",
  failed: "ناموفق",
  refunded: "بازگشت وجه",
};

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString("fa-IR");
  } catch {
    return "-";
  }
};

const postTrackingUrl = (code) => {
  if (!code) return "https://tracking.post.ir/";
  return `https://tracking.post.ir/?id=${encodeURIComponent(code)}`;
};

const copyText = async (value, notify) => {
  if (!value) return;

  try {
    await navigator.clipboard.writeText(value);
    notify("کد رهگیری کپی شد.", "success", "رهگیری سفارش");
  } catch {
    const input = document.createElement("textarea");
    input.value = value;
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
    notify("کد رهگیری کپی شد.", "success", "رهگیری سفارش");
  }
};

const orderStatusText = (order) => {
  return statusLabels[order?.status] || order?.status || "-";
};

const UserOrders = () => {
  const { page: pageParam } = useParams();
  const page = Number(pageParam) > 0 ? Number(pageParam) : 1;

  const { data, isLoading, isFetching, isError } = useGetOrdersQuery(page);
  const [requestPayment] = useRequestPaymentMutation();
  const { notify } = useFeedback();
  const [payingOrder, setPayingOrder] = useState("");

  const orders = data?.orders?.data || [];
  const pagination = data?.orders;

  const canPayOrder = (order) =>
    order?.sales_channel !== "offline" &&
    order?.status !== "cancelled" &&
    order?.payment_status !== "paid" &&
    order?.payment_status !== "refunded";

  const startPayment = async (order) => {
    const ref = orderRef(order);
    if (!ref || payingOrder) return;

    setPayingOrder(ref);

    try {
      const res = await requestPayment(ref).unwrap();

      if (res.payment_url) {
        window.location.href = res.payment_url;
        return;
      }

      notify("درگاه پرداخت آماده نشد. لطفاً دوباره تلاش کنید.", "warning", "پرداخت");
    } catch (err) {
      notify(err?.data?.message || "امکان پرداخت دوباره این سفارش وجود ندارد.", "warning", "پرداخت");
    } finally {
      setPayingOrder("");
    }
  };

  return (
    <>
      <Nav />

      <main className="account-page">
        <div className="my-container">
          <div className="account-shell">
            <AccountList />

            <section className="account-content">
              <div className="account-hero account-hero-compact">
                <div>
                  <span className="account-kicker">سفارش‌ها</span>
                  <h1>سفارش‌های من</h1>
                  <p>نمای ساده وضعیت خرید و ارسال سفارش‌ها</p>
                </div>

                <Link to="/products" className="account-hero-action">
                  خرید جدید
                </Link>
              </div>

              <section className="account-card account-orders-panel-clean">
                {isLoading || isFetching ? (
                  <Spinner />
                ) : isError ? (
                  <div className="account-alert">خطا در دریافت سفارش‌ها. لطفا دوباره تلاش کنید.</div>
                ) : orders.length > 0 ? (
                  <>
                    <div className="account-order-clean-list">
                      {orders.map((order) => (
                        <article className="account-order-clean-card" key={order.id}>
                          <div className="account-order-clean-main">
                            <div className="account-order-clean-header">
                              <div>
                                <span>سفارش</span>
                                <strong>{order.order_number || "-"}</strong>
                              </div>

                              <span className={`account-order-chip status-${order.status || "default"}`}>
                                {orderStatusText(order)}
                              </span>
                            </div>

                            <div className="account-order-clean-meta">
                              <div>
                                <span>تاریخ</span>
                                <strong>{formatDate(order.created_at)}</strong>
                              </div>

                              <div>
                                <span>مبلغ</span>
                                <strong>{toman(order.total || 0)}</strong>
                              </div>

                              <div>
                                <span>پرداخت</span>
                                <strong>{paymentLabels[order.payment_status] || order.payment_status || "-"}</strong>
                              </div>
                            </div>

                            {order.postal_tracking_code && (
                              <div className="account-order-clean-tracking">
                                <FiTruck size={17} />
                                <span dir="ltr">{order.postal_tracking_code}</span>
                                <button type="button" onClick={() => copyText(order.postal_tracking_code, notify)}>
                                  <FiCopy size={14} />
                                </button>
                                <a href={postTrackingUrl(order.postal_tracking_code)} target="_blank" rel="noreferrer">
                                  <FiExternalLink size={14} />
                                </a>
                              </div>
                            )}
                          </div>

                          <div className="account-order-clean-actions">
                            {canPayOrder(order) && (
                              <button
                                type="button"
                                onClick={() => startPayment(order)}
                                disabled={payingOrder === orderRef(order)}
                              >
                                <FiCreditCard size={16} />
                                {payingOrder === orderRef(order) ? "اتصال..." : "پرداخت"}
                              </button>
                            )}

                            <Link to={orderDetailUrl(order)}>
                              <AiOutlineEye size={17} />
                              جزئیات
                            </Link>
                          </div>
                        </article>
                      ))}
                    </div>

                    {pagination?.last_page > 1 && (
                      <div className="account-pagination">
                        <Pagination
                          page={page}
                          perPage={pagination.per_page}
                          count={pagination.total}
                          path="orders"
                          theme="light"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="account-empty-state">
                    <AiOutlineShoppingCart size={42} />
                    <strong>هنوز سفارشی ثبت نکرده‌اید</strong>
                    <p>بعد از خرید، وضعیت سفارش و رهگیری پستی اینجا نمایش داده می‌شود.</p>
                    <Link to="/products">مشاهده محصولات</Link>
                  </div>
                )}
              </section>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserOrders;
